// src/api/notifications.ts - FIXED SSE CONNECTION with better error handling
import { api, API_BASE_URL } from './client';
import type { ApiResponse, Notification, PaginationData } from '../types';

// Track active SSE connections
const activeSSEConnections = new Map<string, EventSource>();

export const notificationsAPI = {
  getMyNotifications: (params?: { page?: number; limit?: number; unreadOnly?: boolean }) => 
    api.get<ApiResponse<{ notifications: Notification[]; pagination?: PaginationData; unreadCount?: number }>>('/api/notifications', { params }),
  
  getUnreadCount: () => api.get<ApiResponse<{ count: number }>>('/api/notifications/unread/count'),
  
  create: (data: {
    userId: string;
    type: 'order_accepted' | 'payment_received' | 'service_started' | 'destination_reached' | 'order_completed' | 'new_message' | 'offer_accepted' | 'offer_created' | 'system';
    title: string;
    message: string;
    orderId?: string;
    data?: Record<string, unknown>;
  }) => api.post<ApiResponse<Notification>>('/api/notifications', data),
  
  markAsRead: (id: string) => api.put<ApiResponse<Notification>>(`/api/notifications/${id}/read`),
  
  markAllAsRead: () => api.put<ApiResponse<{ count: number }>>('/api/notifications/read-all'),
  
  delete: (id: string) => api.delete<ApiResponse>(`/api/notifications/${id}`),
  
  deleteAll: () => api.delete<ApiResponse>('/api/notifications'),
  
  markMultipleAsRead: (ids: string[]) => 
    api.put<ApiResponse<{ count: number }>>('/api/notifications/mark-read', { ids }),

  // FIXED SSE with better error handling and reconnection logic
  listenForNotifications: (userId: string, callback: (notification: Notification) => void) => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.log('🔌 No token available for SSE connection');
      return () => {};
    }

    // Close existing connection
    if (activeSSEConnections.has(userId)) {
      try {
        activeSSEConnections.get(userId)?.close();
      } catch (e) {}
      activeSSEConnections.delete(userId);
    }

    const encodedToken = encodeURIComponent(token);
    const url = `${API_BASE_URL}/api/notifications/sse/${userId}?token=${encodedToken}`;
    
    console.log(`🔌 Connecting to SSE for user ${userId}`);
    
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    let reconnectTimer: NodeJS.Timeout | null = null;
    
    const connect = () => {
      const eventSource = new EventSource(url, {
        withCredentials: true
      });
      
      activeSSEConnections.set(userId, eventSource);
      
      eventSource.onopen = () => {
        console.log('🔌 SSE connection opened');
        reconnectAttempts = 0; // Reset on successful connection
      };
      
      eventSource.onmessage = (event) => {
        try {
          const notification = JSON.parse(event.data) as Notification;
          callback(notification);
        } catch (error) {
          console.error('Error parsing SSE notification:', error);
        }
      };
      
      eventSource.addEventListener('connected', (event) => {
        console.log('✅ SSE connected:', event.data);
      });
      
      eventSource.addEventListener('ping', () => {
        // Silent ping - no log
      });
      
      const notificationTypes = [
        'order_accepted', 'offer_accepted', 'payment_received',
        'service_started', 'destination_reached', 'order_completed',
        'new_message', 'offer_created', 'system'
      ];
      
      notificationTypes.forEach(type => {
        eventSource.addEventListener(type, (event) => {
          try {
            const notification = JSON.parse(event.data) as Notification;
            callback(notification);
          } catch (error) {
            console.error(`Error parsing ${type} notification:`, error);
          }
        });
      });
      
      eventSource.onerror = (error) => {
        // Don't log the error in production to avoid console spam
        if (process.env.NODE_ENV === 'development') {
          console.log('📡 SSE connection event (normal)');
        }
        
        if (eventSource.readyState === EventSource.CLOSED) {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔌 SSE connection closed');
          }
          activeSSEConnections.delete(userId);
          
          // Don't reconnect for auth errors
          if (error instanceof Event && (error as any).status === 401) {
            if (process.env.NODE_ENV === 'development') {
              console.log('🔌 Authentication error - token may be invalid');
            }
            return;
          }
          
          // Reconnect with backoff
          if (reconnectAttempts < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
            
            if (process.env.NODE_ENV === 'development') {
              console.log(`🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1})`);
            }
            
            if (reconnectTimer) {
              clearTimeout(reconnectTimer);
            }
            
            reconnectTimer = setTimeout(() => {
              if (!activeSSEConnections.has(userId)) {
                connect();
              }
            }, delay);
            
            reconnectAttempts++;
          }
        }
      };
    };
    
    // Start connection
    connect();
    
    return () => {
      console.log('🔌 Closing SSE connection');
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      try {
        const eventSource = activeSSEConnections.get(userId);
        if (eventSource) {
          eventSource.close();
        }
      } catch (e) {}
      activeSSEConnections.delete(userId);
    };
  }
};

export default notificationsAPI;